import clsx from "clsx";
import { parseISO } from "date-fns";
import { FC, Ref, useCallback, useRef, useState } from "react";
import { useBoolean } from "usehooks-ts";

import { CopyToSectionSubMenu } from "./CopyToSectionSubMenu";
import { MoveToSubMenu } from "./MoveToSubMenu";
import { SidebarOptionsMenu, SidebarOptionsMenuProps } from "./SidebarOptionsMenu";

import { AssessmentStatus } from "api/api-data-types";
import { STATUS_COLORS } from "components/materials/page/helpers";
import { useIsAssessing } from "components/materials/section/assessment/hooks";
import { ActivityCategory, PageType, SectionPageCategory } from "components/server-types";
import { toastReleasePage, toastUnreleasePage } from "helpers/toasts";
import { t } from "i18n/i18n";
import { ConfirmDeletionDialog } from "mds/components/ConfirmDeletionDialog";
import { IconText } from "mds/components/IconText";
import { MenuDivider, MenuHeader, MenuInstance, MenuItem, SubMenu } from "mds/components/Menu";
import { showConfirmationModal } from "mds/components/Modal";
import {
  AssessPointsMarkerIcon,
  CheckIcon,
  CopySendArrowIcon,
  DoubleSparkleIcon,
  LockLockedIcon,
  LockUnlockedIcon,
  PageStatusIcon,
  PersonClassIcon,
  UndoIcon,
} from "mds/icons";
import { useWorksheetHasBlocks } from "sharedb/ShareDBDoc";
import { useAppDispatch, useAppSelector } from "store/index";
import {
  activitySelectors,
  selectAssessedSubmissionsByPageId,
  selectCountUsersWhoCanParticipateInSection,
  selectCurrentSectionIsArchived,
  selectCurrentSectionIsLinked,
  selectCurrentSectionIsTemplate,
  selectRedirectUrlForPageDeletion,
} from "store/selectors";
import { selectAssessmentInfoForPage } from "store/selectors/computed";
import { computedStateActions } from "store/slices/computed";
import { useListSelector } from "store/store-hooks";
import { toastSuccessMessage } from "utils/alerts";
import { BUTTON_EVENT_CATEGORY, trackEvent } from "utils/amplitude";
import { isSectionPage, markPageAssessmentsStatus, usePageActions } from "utils/page";
import { isPageDisabled, isSharedClassPage, isStaticPage } from "utils/page-utils";
import { usePresentation } from "utils/presentation";
import { useNavigate } from "utils/routing";
import { useSubmissions } from "utils/submission";
import { pageUrl } from "utils/urls";

const COLLECTION_DISABLED_TIMEOUT_MS = 10000;

export interface PageSidebarOptionsMenuProps
  extends Omit<SidebarOptionsMenuProps, "optionsMenuActions"> {
  page: PageType;
  currentTopicId?: string;
  editMode?: boolean;
  showDownloadUploadOptions?: boolean;
  showTimeline?: boolean;
  kind?: "ghost" | "secondary";
  size?: "xs" | "s";
  onSummarizeResponses?: () => void;
}

export const PageSidebarOptionsMenu: FC<PageSidebarOptionsMenuProps> = ({
  styles,
  className,
  direction = "right",
  page,
  currentTopicId,
  editMode,
  kind,
  size = "s",
  onSummarizeResponses,
  showDownloadUploadOptions,
  showTimeline,
}) => {
  const navigate = useNavigate();
  const isInTemplateSection = useAppSelector(selectCurrentSectionIsTemplate);
  const isInLinkedSection = useAppSelector(selectCurrentSectionIsLinked);
  const isArchived = useAppSelector(selectCurrentSectionIsArchived);
  const isDisabled = isPageDisabled(page);
  const isReleased = !!page?.released_at;
  const isLocked = !!page?.locked_at;

  const [isCollectDisabled, setIsCollectDisabled] = useState(false);
  const pageHasBlocks = useWorksheetHasBlocks(page?.worksheet_id) ?? false;
  const redirectUrl = useAppSelector((s) => selectRedirectUrlForPageDeletion(s, page?.id));
  const { copyPage, deletePage, lockPage, unlockPage, savePage, movePageWithinSection } =
    usePageActions();
  const { presentedPageId } = usePresentation();
  const activity = useAppSelector((s) => activitySelectors.selectById(s, page.activity_id));
  const nStudents = useAppSelector(selectCountUsersWhoCanParticipateInSection);
  const dispatch = useAppDispatch();
  const isAssessing = useIsAssessing();
  const canLock = !isStaticPage(page);

  const { createSubmissions } = useSubmissions(page);

  const assessmentInfo = useAppSelector((s) => selectAssessmentInfoForPage(s, page.id));
  const eventProps = {
    pageTitle: page?.title,
    pageUrl: pageUrl(page?.section_id, page?.id),
    pageId: page?.id,
  };

  const isMarkedComplete = !!page?.assessing_marked_complete_at;
  const canMarkComplete = assessmentInfo?.status === AssessmentStatus.ASSESSABLE && !isAssessing;

  const onMarkAssessmentComplete = async () => {
    await markPageAssessmentsStatus(page.id, !isMarkedComplete);
  };

  const {
    value: confirmationDialogIsOpen,
    setTrue: openConfirmationDialog,
    setFalse: closeConfirmationDialog,
  } = useBoolean(false);

  const pageReleasable =
    page.category !== "instructor_workspace" &&
    (!activity || activity?.category !== ActivityCategory.ARCHIVE);
  const showCopyToSection = isSectionPage(page);

  const menuRef: Ref<MenuInstance> = useRef(null);
  const assessedSubmissions = useListSelector((s) =>
    selectAssessedSubmissionsByPageId(s, page?.id),
  );

  const hasAssessments = assessedSubmissions.length > 0;

  const canDeletePage = !hasAssessments && presentedPageId !== page.id;
  const cannotSummarize =
    isSharedClassPage(page) || nStudents === 0 || isStaticPage(page) || !isReleased;
  const cannotCollect = cannotSummarize || isCollectDisabled;
  const cannotReleaseClassResponses = cannotSummarize;

  const disableCollectionTemporarily = () => {
    setIsCollectDisabled(true);
    setTimeout(() => setIsCollectDisabled(false), COLLECTION_DISABLED_TIMEOUT_MS);
  };

  const closeMenu = () => menuRef.current?.closeMenu();

  // Menu actions
  const onRelease = async () => {
    await savePage({ id: page.id, released_at: new Date().toJSON() });
    toastReleasePage();
  };

  const performUnrelease = async () => {
    await savePage({ id: page.id, released_at: null });
    toastUnreleasePage();
  };

  const onUnrelease = async () => {
    if (page.assessments_published_at) {
      const confirmed = await showConfirmationModal({
        title: t("sidebar_options_menu.unrelease_with_assessments_title"),
        labelText: (
          <div className="whitespace-pre-line">
            {t("sidebar_options_menu.unrelease_with_assessments")}
          </div>
        ),
        confirmButtonText: t("sidebar_options_menu.unrelease_page"),
        isWarningLabel: true,
      });

      if (!confirmed) {
        return;
      }
    }

    await performUnrelease();
  };

  const onSetReleaseClassResponses = async () => {
    const releaseDate = page.class_responses_released_at ? null : new Date().toJSON();

    await savePage({ id: page.id, class_responses_released_at: releaseDate });

    toastSuccessMessage(
      releaseDate
        ? t("sidebar_options_menu.toasts.class_pages_released")
        : t("sidebar_options_menu.toasts.class_pages_unreleased"),
    );
  };

  const onLock = () => lockPage(page);
  const onUnlock = () => unlockPage(page);

  const onCollect = async () => {
    await createSubmissions();
    disableCollectionTemporarily();

    // Mark page as ready for assessment
    dispatch(
      computedStateActions.savePageAssessmentStatuses({
        [page.id]: {
          status: AssessmentStatus.ASSESSABLE,
          unassessed_late_submissions_count: 0,
          assessed_submissions_count: 0,
          unseen_late_submissions_count: 0,
          unseen_submissions_count: 0,
        },
      }),
    );
  };

  const deletePageAndRedirect = useCallback(async () => {
    await deletePage(page);
    if (redirectUrl) {
      navigate(redirectUrl);
    }
  }, [deletePage, navigate, page, redirectUrl]);

  const onDelete = useCallback(() => {
    trackEvent("Page - Delete", {
      ...BUTTON_EVENT_CATEGORY,
      context: "page_sidebar",
    });
    if (pageHasBlocks) {
      openConfirmationDialog();
    } else {
      deletePageAndRedirect();
    }
  }, [deletePageAndRedirect, openConfirmationDialog, pageHasBlocks]);

  const onDuplicate = () => {
    const dupProps = page.activity_id
      ? { activityIds: [page.activity_id] }
      : { sectionIds: [page.section_id] };

    trackEvent("Page - Duplicate", {
      ...BUTTON_EVENT_CATEGORY,
      context: "page_sidebar",
    });
    copyPage(page.category, page.id, dupProps);
  };

  const onMoveTo = (destinationCategory: SectionPageCategory, destinationTopicId?: string) => {
    movePageWithinSection(page, { destinationCategory, destinationTopicId });
    trackEvent("Page - Move to", {
      ...BUTTON_EVENT_CATEGORY,
      context: "page_sidebar",
    });
    closeMenu();
  };

  const onCopyToSection = (sectionIds: string[]) => {
    copyPage(page.category, page.id, { sectionIds });
    trackEvent("Page - Copy to section", {
      ...BUTTON_EVENT_CATEGORY,
      context: "page_sidebar",
    });
    closeMenu();
  };

  return (
    <SidebarOptionsMenu
      className={className}
      direction={direction}
      eventPrefix="Page - Options / "
      instanceRef={menuRef}
      kind={kind}
      limitItemHeight={false}
      optionsMenuActions={{
        onDelete: isInLinkedSection ? undefined : onDelete,
        onDuplicate: isInLinkedSection ? undefined : onDuplicate,
        isDeleteDisabled:
          !canDeletePage || !editMode || (isInTemplateSection && isDisabled) || isArchived,
        isDuplicateDisabled: !editMode || isArchived,
        deleteTooltipText: canDeletePage
          ? undefined
          : hasAssessments
            ? t("tooltip.delete_page_has_assessments")
            : t("tooltip.delete_page_presenting"),
        page,
        showDownloadUploadOptions,
        isUploadDisabled: isArchived,
        showTimeline,
      }}
      size={size}
      styles={styles}
    >
      {!isInTemplateSection && (
        <>
          <MenuHeader className="text-xs text-black-tint-55">
            {t("sidebar_options_menu.page_status")}
          </MenuHeader>
          {pageReleasable && (
            <>
              {isReleased ? (
                <MenuItem
                  disabled={presentedPageId === page.id || isArchived}
                  eventName="Page - Options / Unrelease"
                  eventProps={eventProps}
                  onClick={onUnrelease}
                >
                  <IconText
                    iconStart={<PageStatusIcon statusColor={STATUS_COLORS.unreleased} />}
                    text={t("sidebar_options_menu.unrelease_page")}
                  />
                </MenuItem>
              ) : (
                <MenuItem
                  className="hover:!bg-green-tint-90"
                  disabled={isArchived}
                  eventName="Page - Options / Release"
                  eventProps={eventProps}
                  onClick={onRelease}
                >
                  <IconText
                    iconStart={<PageStatusIcon statusColor={STATUS_COLORS.released} />}
                    text={t("sidebar_options_menu.release_page")}
                  />
                </MenuItem>
              )}
              {isLocked ? (
                <MenuItem
                  disabled={!canLock || isArchived}
                  eventName="Page - Options / Unlock page"
                  onClick={onUnlock}
                >
                  <IconText
                    iconStart={<LockUnlockedIcon />}
                    text={t("sidebar_options_menu.unlock")}
                  />
                </MenuItem>
              ) : (
                <MenuItem
                  disabled={!canLock || isArchived}
                  eventName="Page - Options / Lock page"
                  onClick={onLock}
                >
                  <IconText iconStart={<LockLockedIcon />} text={t("sidebar_options_menu.lock")} />
                </MenuItem>
              )}
              <MenuDivider />

              <MenuHeader className="text-xs text-black-tint-55">
                {t("sidebar_options_menu.page_responses")}
              </MenuHeader>

              {onSummarizeResponses && (
                <MenuItem
                  disabled={cannotSummarize}
                  eventName="Page - Options / Summarize responses"
                  eventProps={eventProps}
                  title={
                    cannotSummarize ? t("sidebar_options_menu.tooltip.no_responses") : undefined
                  }
                  onClick={onSummarizeResponses}
                >
                  <IconText
                    iconStart={
                      <DoubleSparkleIcon
                        className={clsx(cannotSummarize && "text-black-tint-55")}
                      />
                    }
                    text={t("sidebar_options_menu.ai_summarize")}
                  />
                </MenuItem>
              )}

              <MenuItem
                disabled={cannotCollect || isArchived}
                eventName="Page - Options / Collect responses"
                eventProps={eventProps}
                title={cannotCollect ? t("sidebar_options_menu.tooltip.no_responses") : undefined}
                onClick={onCollect}
              >
                <div className="flex items-start gap-2">
                  <AssessPointsMarkerIcon className="mt-1" />
                  <div className="flex flex-col">
                    <div>{t("sidebar_options_menu.collect")}</div>
                    {page.submissions_last_collected_at && (
                      <div className="text-xs text-green-shade-20">
                        {t("sidebar_options_menu.last_collected_at", {
                          date: parseISO(page.submissions_last_collected_at),
                        })}
                      </div>
                    )}
                  </div>
                </div>
              </MenuItem>

              {page.class_responses_released_at ? (
                <MenuItem
                  disabled={isArchived}
                  eventName="Page - Options / Unrelease class responses"
                  eventProps={eventProps}
                  onClick={onSetReleaseClassResponses}
                >
                  <IconText
                    iconStart={<PersonClassIcon />}
                    text={t("sidebar_options_menu.unrelease_class_responses")}
                  />
                </MenuItem>
              ) : (
                <MenuItem
                  disabled={cannotReleaseClassResponses || isArchived}
                  eventName="Page - Options / Release class responses"
                  eventProps={eventProps}
                  title={
                    cannotReleaseClassResponses
                      ? t("sidebar_options_menu.tooltip.no_submissions")
                      : undefined
                  }
                  onClick={onSetReleaseClassResponses}
                >
                  <IconText
                    iconStart={<PersonClassIcon />}
                    text={t("sidebar_options_menu.release_class_responses")}
                  />
                </MenuItem>
              )}

              {onMarkAssessmentComplete && (
                <MenuItem
                  disabled={!canMarkComplete || isArchived}
                  eventName={`Page Overview / ${isMarkedComplete ? "Undo mark as complete" : "Mark as complete"}`}
                  eventProps={eventProps}
                  onClick={onMarkAssessmentComplete}
                >
                  {isMarkedComplete ? (
                    <IconText
                      iconStart={<UndoIcon />}
                      text={t("sidebar_options_menu.mark_assessments_as_complete_undo")}
                    />
                  ) : (
                    <IconText
                      iconStart={<CheckIcon />}
                      text={t("sidebar_options_menu.mark_assessments_as_complete")}
                    />
                  )}
                </MenuItem>
              )}

              {!isInLinkedSection && <MenuDivider />}
            </>
          )}
        </>
      )}

      {showCopyToSection && (
        <SubMenu
          disabled={!editMode || isArchived}
          label={
            <IconText
              iconStart={<CopySendArrowIcon />}
              text={t("sidebar_options_menu.copy_to_section")}
            />
          }
          withArrow
        >
          <CopyToSectionSubMenu
            category={page.category}
            onCancel={closeMenu}
            onConfirm={onCopyToSection}
          />
        </SubMenu>
      )}

      {!isInLinkedSection && (
        <SubMenu
          disabled={!editMode || isArchived}
          label={
            <IconText iconStart={<CopySendArrowIcon />} text={t("sidebar_options_menu.move_to")} />
          }
          overflow="auto"
          withArrow
        >
          <MoveToSubMenu
            currentPageCategory={page?.category}
            currentTopicId={currentTopicId}
            itemKind="page"
            onCancel={closeMenu}
            onConfirm={onMoveTo}
          />
        </SubMenu>
      )}

      <ConfirmDeletionDialog
        glossaryKey="page"
        instanceName={page.title}
        open={confirmationDialogIsOpen}
        onClose={closeConfirmationDialog}
        onDelete={deletePageAndRedirect}
      />
    </SidebarOptionsMenu>
  );
};
